import { apiService } from './api';
import type {
  BillingCatalog,
  BillingCompany,
  BillingCompanyConfirmation,
  BillingCompanyIdentification,
  BillingCheckoutSession,
  BillingCheckoutStatus,
  BillingPortalSession,
  BillingPriceKey,
  BillingSubscriptionCancellation,
  BillingSubscriptionOffer,
  BillingSubscriptionResumption,
  BillingSubscriptionStatus,
  BillingSubscriptionDowngrade,
  BillingSubscriptionDowngradePreview,
  BillingEnterpriseSubscriptionPriceKey,
} from '../types/billing';
import { analyticsIdentityService } from './analytics/AnalyticsIdentity.service';
import { cookieConsentService } from './cookieConsent';
import { buildPaymentLinkPath } from '../config/paymentLinkSeo';
import { addBillingCheckoutContext, type BillingCheckoutContext } from '../utils/billingCheckoutContext';
import { subscriptionChangeReturnUrl } from '../utils/billingSubscriptionChange';

export type BillingLocale = 'en' | 'fr';
export type BillingCheckoutOptions = { source: 'payment_link'; priceId: string } | { source: 'app_modal'; returnContext: BillingCheckoutContext };

function checkoutReturnUrl(result: 'success' | 'cancel', context?: BillingCheckoutContext): string {
  const url = new URL(window.location.href);
  url.searchParams.set('billing', result);
  addBillingCheckoutContext(url, result === 'cancel' ? context : undefined);
  url.searchParams.delete('billing_session_id');
  return url.toString();
}

function paymentLinkReturnUrl(priceId: string, locale: BillingLocale, result: 'success' | 'cancel'): string {
  const paymentPath = buildPaymentLinkPath(priceId, locale);
  const path = result === 'success' ? locale === 'en' ? '/en/' : '/' : paymentPath;
  const url = new URL(path, window.location.origin);
  if (result === 'success') url.searchParams.set('billing', result);
  return url.toString();
}

export class BillingService {
  async catalog(): Promise<BillingCatalog> {
    return apiService.get<BillingCatalog>('/billing/catalog', undefined, true);
  }

  async company(): Promise<BillingCompany | null> {
    return apiService.get<BillingCompany | null>('/billing/company');
  }

  async identifyCompany(identifier: string): Promise<BillingCompanyIdentification> {
    return apiService.request<BillingCompanyIdentification>('/billing/company/identify', {
      method: 'POST',
      body: { identifier },
      retry: false,
    });
  }

  async confirmCompany(input: BillingCompanyConfirmation): Promise<BillingCompany> {
    return apiService.post<BillingCompany>('/billing/company/confirm', input);
  }

  async createCheckout(priceKey: BillingPriceKey, locale: BillingLocale, options?: BillingCheckoutOptions): Promise<BillingCheckoutSession> {
    const analyticsPseudonymous = !cookieConsentService.canUseAudienceMeasurement();
    const isPaymentLink = options?.source === 'payment_link';
    return apiService.post<BillingCheckoutSession>(isPaymentLink ? '/billing/payment-link-checkout' : '/billing/checkout', {
      priceKey,
      successUrl: options?.source === 'payment_link' ? paymentLinkReturnUrl(options.priceId, locale, 'success') : checkoutReturnUrl('success'),
      cancelUrl: options?.source === 'payment_link' ? paymentLinkReturnUrl(options.priceId, locale, 'cancel') : checkoutReturnUrl('cancel', options?.returnContext),
      locale,
      analyticsDistinctId: analyticsPseudonymous
        ? analyticsIdentityService.refusedId()
        : analyticsIdentityService.consentedId(),
      analyticsPseudonymous,
    });
  }

  async redirectToCheckout(priceKey: BillingPriceKey, locale: BillingLocale, options?: BillingCheckoutOptions): Promise<void> {
    const session = await this.createCheckout(priceKey, locale, options);
    window.location.assign(session.url);
  }

  async checkoutStatus(sessionId: string): Promise<BillingCheckoutStatus> {
    return apiService.get<BillingCheckoutStatus>(
      `/billing/checkout/${encodeURIComponent(sessionId)}/status`,
    );
  }

  async createPortalSession(locale: BillingLocale): Promise<BillingPortalSession> {
    return apiService.post<BillingPortalSession>('/billing/portal', {
      returnUrl: window.location.href,
      locale,
    });
  }

  async openBillingPortal(locale: BillingLocale): Promise<void> {
    const portalWindow = window.open('about:blank', '_blank');
    if (!portalWindow) {
      throw new Error('The billing portal tab could not be opened');
    }
    portalWindow.opener = null;
    try {
      const session = await this.createPortalSession(locale);
      portalWindow.location.replace(session.url);
    } catch (error) {
      portalWindow.close();
      throw error;
    }
  }

  async subscription(): Promise<BillingSubscriptionStatus> {
    return apiService.get('/billing/subscription');
  }

  async createSubscriptionChange(priceKey: BillingEnterpriseSubscriptionPriceKey, locale: BillingLocale): Promise<{ url: string }> {
    return apiService.request('/billing/subscription/change', {
      method: 'POST', body: { priceKey, locale, returnUrl: subscriptionChangeReturnUrl() }, retry: false,
    });
  }

  async redirectToSubscriptionChange(priceKey: BillingEnterpriseSubscriptionPriceKey, locale: BillingLocale): Promise<void> {
    const session = await this.createSubscriptionChange(priceKey, locale);
    window.location.assign(session.url);
  }

  async subscriptionOffer(): Promise<BillingSubscriptionOffer> {
    return apiService.get('/billing/subscription/offer');
  }

  async previewSubscriptionDowngrade(): Promise<BillingSubscriptionDowngradePreview> {
    return apiService.get('/billing/subscription/downgrade');
  }

  async confirmSubscriptionDowngrade(quote: string): Promise<BillingSubscriptionDowngrade> {
    return apiService.request('/billing/subscription/downgrade', {
      method: 'POST', body: { confirmed: true, quote }, retry: false,
    });
  }

  async cancelSubscriptionDowngrade(): Promise<{ cancelled: boolean }> {
    return apiService.request('/billing/subscription/downgrade/cancel', {
      method: 'POST', body: {}, retry: false,
    });
  }

  async cancelSubscription(): Promise<BillingSubscriptionCancellation> {
    return apiService.post('/billing/subscription/cancel');
  }

  async resumeSubscription(): Promise<BillingSubscriptionResumption> {
    return apiService.post('/billing/subscription/resume');
  }

  async deleteAccount(): Promise<{
    deleted: boolean;
    cancelledSubscriptions: number;
    groupCode: string;
  }> {
    return apiService.request('/billing/account', {
      method: 'DELETE',
      retry: false,
    });
  }
}

export const billingService = new BillingService();
